'use client';

import { amplitudeSendEvent, getScreenName } from '@musinsa/frontend-utils';
import {
  BottomSheetHandleArea,
  BottomSheetTitle,
  BottomSheetTitleBox,
  BottomSheetTitleContainer,
  Dim,
  IcDelete,
  IconButton,
  Sheet,
  SheetContent,
  SheetDimMotion,
  SheetMotion,
  SheetPortal,
  SheetTrigger,
  Typography,
} from '@musinsa/mds';
import { SERVICE, useDeviceInfo } from '@musinsa/packages-device-detection';
import { track } from '@musinsa/packages-event-snowplow';
import type { SnsLink } from '@musinsa/services-curator';
import { useParams } from 'next/navigation';
import type { MouseEventHandler } from 'react';
import { useState } from 'react';
import { twMerge } from 'tailwind-merge';
import { useTrackingData } from '../../common/components/TrackingDataProvider';
import { SNOWPLOW_SCHEME } from '../../snowplow';

interface Props {
  className?: string;
  snsLinks: SnsLink[];
}

export const MyShopSnsLinks = ({ className, snsLinks }: Props) => {
  const params = useParams();
  const { data: trackingData } = useTrackingData();
  const { service } = useDeviceInfo();
  const isMusinsa = service === SERVICE.MUSINSA;
  const [isSheetOpen, setIsSheetOpen] = useState(false);
  const isMultipleLinks = snsLinks.length > 1;
  const primaryLink = snsLinks[0];
  const curatorId = params.shopSlug as string;

  const onLinkClick =
    (url: string): MouseEventHandler<HTMLElement> =>
    () => {
      setIsSheetOpen(false);

      if (isMusinsa) {
        amplitudeSendEvent({
          action_name: 'click',
          button_name: 'profile_link_click',
          component_name: 'button',
          event_name: 'click_button',
          from: getScreenName(),
          id: curatorId,
          section_name: 'curator_profile',
        });
      }
      if (service === SERVICE['29CM']) {
        track({
          schema: SNOWPLOW_SCHEME.CLICK_BUTTON,
          data: {
            ...trackingData,
            section_name: 'CurationShopInfo',
            section_title: 'CurationShopInfo',
            button_id: 'enter_page',
            button_name: 'SNS 링크',
            position: -1,
            reward_program_key: 'curator_shop',
            reward_key: `curator_${params.shopSlug}`,
          },
        });
      }

      window.open(url, '_blank');
    };

  const onSheetOpen = () => {
    if (isMusinsa) {
      amplitudeSendEvent({
        action_name: 'click',
        button_name: 'profile_link_open',
        component_name: 'button',
        event_name: 'click_button',
        from: getScreenName(),
        id: curatorId,
        section_name: 'curator_profile',
      });
    }
    setIsSheetOpen(true);
  };

  const onSheetClose = () => {
    setIsSheetOpen(false);
  };

  if (isMultipleLinks) {
    return (
      <div className={twMerge(className)}>
        <Sheet direction="bottom" open={isSheetOpen} onOpenChange={setIsSheetOpen}>
          <SheetTrigger asChild onClick={onSheetOpen}>
            <button
              type="button"
              className="flex gap-[2px] cursor-pointer w-full items-start overflow-hidden"
            >
              <LinkIcon className="shrink-0 mt-[1px]" />
              <Typography
                variant="body13pxReg"
                className="underline text-left"
                wordBreak="breakAll"
                lineHeight="18"
              >
                {`${primaryLink.url} 외 ${snsLinks.length - 1}개`}
              </Typography>
            </button>
          </SheetTrigger>
          <SheetPortal>
            <SheetContent onPointerDownOutside={onSheetClose}>
              <SheetMotion direction="bottom">
                <BottomSheetTitle>
                  <BottomSheetHandleArea />
                  <BottomSheetTitleContainer>
                    <BottomSheetTitleBox>SNS</BottomSheetTitleBox>
                    <IconButton size="size28x28" onClick={onSheetClose}>
                      <IcDelete />
                    </IconButton>
                  </BottomSheetTitleContainer>
                </BottomSheetTitle>
                <div className="flex flex-col px-[16px]! safe-area-bottom">
                  {snsLinks.map((snsLink) => (
                    <Typography
                      key={snsLink.url}
                      colorScheme="gray-600"
                      variant="body13pxReg"
                      className="py-[12px]! space-y-[11px] cursor-pointer"
                      onClick={onLinkClick(snsLink.url)}
                    >
                      {snsLink.url}
                    </Typography>
                  ))}
                </div>
              </SheetMotion>
            </SheetContent>

            <SheetDimMotion>
              <Dim />
            </SheetDimMotion>
          </SheetPortal>
        </Sheet>
      </div>
    );
  }

  return (
    <div className={twMerge(className)}>
      <button
        type="button"
        onClick={onLinkClick(primaryLink.url)}
        className="flex gap-[2px] cursor-pointer"
      >
        <LinkIcon />
        <Typography variant="body13pxReg" className="underline text-left" wordBreak="breakAll">
          {primaryLink.url}
        </Typography>
      </button>
    </div>
  );
};

const LinkIcon = ({ className }: { className?: string }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="18"
    height="18"
    viewBox="0 0 18 18"
    fill="none"
    className={className}
  >
    <path
      d="M5.90458 7.7619L4.04739 9.61909C2.85074 10.8157 2.85074 12.7559 4.04739 13.9525C5.24404 15.1492 7.18419 15.1492 8.38084 13.9525L10.238 12.0953M6.52365 11.4763L11.4762 6.52377M7.76177 5.9047L9.61897 4.04751C10.8156 2.85086 12.7558 2.85086 13.9524 4.04751C15.1491 5.24416 15.1491 7.18431 13.9524 8.38096L12.0952 10.2382"
      stroke="black"
      strokeWidth="1.4"
    />
  </svg>
);
